import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  LinearProgress,
  Tooltip,
  Badge,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Edit,
  Delete,
  Refresh,
  Add,
  Inventory,
  VisibilityOff,
  Star,
  StarBorder,
  Category,
  AttachMoney,
  MoreVert
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const SellerStore = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    stock: '',
    image: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, categoryFilter, statusFilter]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual API call
      const mockProducts = [
        {
          id: 1,
          name: 'Áo thun nam',
          description: 'Áo thun nam chất liệu cotton 100%, thoáng mát, dễ mặc',
          price: 150000,
          category: 'Áo',
          stock: 50,
          status: 'active',
          image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=150&h=150&fit=crop',
          rating: 4.5,
          sales: 45
        },
        {
          id: 2,
          name: 'Quần jean nữ',
          description: 'Quần jean nữ form slim, chất liệu denim cao cấp',
          price: 500000,
          category: 'Quần',
          stock: 30,
          status: 'active',
          image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=150&h=150&fit=crop',
          rating: 4.8,
          sales: 32
        },
        {
          id: 3,
          name: 'Váy đầm',
          description: 'Váy đầm nữ thiết kế đẹp, phù hợp nhiều dịp',
          price: 800000,
          category: 'Váy',
          stock: 25,
          status: 'inactive',
          image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=150&h=150&fit=crop',
          rating: 4.2,
          sales: 28
        }
      ];

      setProducts(mockProducts);
      toast.success('Đã tải danh sách sản phẩm!');
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Có lỗi xảy ra khi tải sản phẩm');
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(product => product.category === categoryFilter);
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(product => product.status === statusFilter);
    }

    setFilteredProducts(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Đang bán';
      case 'inactive':
        return 'Tạm ngưng';
      default:
        return status;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setShowProductDetail(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setNewProduct({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      stock: product.stock.toString(),
      image: product.image
    });
    setShowAddProduct(true);
  };

  const handleAddProduct = () => {
    setNewProduct({
      name: '',
      description: '',
      price: '',
      category: '',
      stock: '',
      image: ''
    });
    setShowAddProduct(true);
  };

  const handleSaveProduct = async () => {
    try {
      if (selectedProduct) {
        // Update existing product
        setProducts(prev => 
          prev.map(product => 
            product.id === selectedProduct.id 
              ? { ...product, ...newProduct, price: parseFloat(newProduct.price), stock: parseInt(newProduct.stock) }
              : product
          )
        );
        toast.success('Đã cập nhật sản phẩm!');
      } else {
        // Add new product
        const newProductData = {
          id: Date.now(),
          ...newProduct,
          price: parseFloat(newProduct.price),
          stock: parseInt(newProduct.stock),
          status: 'active',
          rating: 0,
          sales: 0
        };
        setProducts(prev => [...prev, newProductData]);
        toast.success('Đã thêm sản phẩm mới!');
      }
      setShowAddProduct(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Có lỗi xảy ra khi lưu sản phẩm');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa sản phẩm này?')) {
      try {
        setProducts(prev => prev.filter(product => product.id !== productId));
        toast.success('Đã xóa sản phẩm!');
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Có lỗi xảy ra khi xóa sản phẩm');
      }
    }
  };

  const handleToggleStatus = async (productId) => {
    try {
      setProducts(prev => 
        prev.map(product => 
          product.id === productId 
            ? { ...product, status: product.status === 'active' ? 'inactive' : 'active' }
            : product
        )
      );
      toast.success('Đã cập nhật trạng thái sản phẩm!');
    } catch (error) {
      console.error('Error toggling product status:', error);
      toast.error('Có lỗi xảy ra khi cập nhật trạng thái');
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <IconButton key={i} size="small" disabled>
          {i <= rating ? <Star sx={{ color: 'warning.main' }} /> : <StarBorder />}
        </IconButton>
      );
    }
    return stars;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Quản lý sản phẩm
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Quản lý và theo dõi tất cả sản phẩm của cửa hàng
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchProducts}
              disabled={loading}
            >
              Làm mới
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddProduct}
            >
              Thêm sản phẩm
            </Button>
          </Box>
        </Box>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Tìm kiếm sản phẩm..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Danh mục</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    label="Danh mục"
                  >
                    <MenuItem value="all">Tất cả</MenuItem>
                    <MenuItem value="Áo">Áo</MenuItem>
                    <MenuItem value="Quần">Quần</MenuItem>
                    <MenuItem value="Váy">Váy</MenuItem>
                    <MenuItem value="Giày">Giày</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Trạng thái</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Trạng thái"
                  >
                    <MenuItem value="all">Tất cả</MenuItem>
                    <MenuItem value="active">Đang bán</MenuItem>
                    <MenuItem value="inactive">Tạm ngưng</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    onClick={() => {
                      setSearchTerm('');
                      setCategoryFilter('all');
                      setStatusFilter('all');
                    }}
                  >
                    Xóa bộ lọc
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </motion.div>

      {/* Products Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Danh sách sản phẩm ({filteredProducts.length})
              </Typography>
            </Box>
            
            {loading ? (
              <Box sx={{ width: '100%' }}>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  Đang tải dữ liệu...
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sản phẩm</TableCell>
                      <TableCell>Danh mục</TableCell>
                      <TableCell>Giá</TableCell>
                      <TableCell>Tồn kho</TableCell>
                      <TableCell>Đánh giá</TableCell>
                      <TableCell>Trạng thái</TableCell>
                      <TableCell align="center">Thao tác</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <AnimatePresence>
                      {filteredProducts.map((product, index) => (
                        <motion.tr
                          key={product.id}
                          component={TableRow}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          hover
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar
                                src={product.image}
                                variant="rounded"
                                sx={{ width: 50, height: 50 }}
                              />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {product.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {product.description.substring(0, 50)}...
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={product.category} size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                              {formatCurrency(product.price)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {product.stock} cái
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {renderStars(product.rating)}
                              <Typography variant="caption" sx={{ ml: 1 }}>
                                ({product.rating})
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusText(product.status)}
                              color={getStatusColor(product.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Tooltip title="Xem chi tiết">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewProduct(product)}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Chỉnh sửa">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditProduct(product)}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={product.status === 'active' ? 'Tạm ngưng' : 'Kích hoạt'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleStatus(product.id)}
                                >
                                  {product.status === 'active' ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Xóa sản phẩm">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteProduct(product.id)}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {filteredProducts.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                                 <Inventory sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Không có sản phẩm nào
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all'
                    ? 'Thử thay đổi bộ lọc để tìm sản phẩm'
                    : 'Chưa có sản phẩm nào được tạo'
                  }
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Product Detail Dialog */}
      <Dialog
        open={showProductDetail}
        onClose={() => setShowProductDetail(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedProduct && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <DialogTitle>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                 <Inventory />
                 <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                   Chi tiết sản phẩm
                 </Typography>
               </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <img
                    src={selectedProduct.image}
                    alt={selectedProduct.name}
                    style={{ width: '100%', height: 300, objectFit: 'cover', borderRadius: 8 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {selectedProduct.name}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedProduct.description}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {formatCurrency(selectedProduct.price)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Danh mục: {selectedProduct.category}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Tồn kho: {selectedProduct.stock} cái
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Đã bán: {selectedProduct.sales} cái
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Đánh giá: {selectedProduct.rating}/5
                    </Typography>
                    {renderStars(selectedProduct.rating)}
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={getStatusText(selectedProduct.status)}
                      color={getStatusColor(selectedProduct.status)}
                    />
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowProductDetail(false)}>
                Đóng
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setShowProductDetail(false);
                  handleEditProduct(selectedProduct);
                }}
              >
                Chỉnh sửa
              </Button>
            </DialogActions>
          </motion.div>
        )}
      </Dialog>

      {/* Add/Edit Product Dialog */}
      <Dialog
        open={showAddProduct}
        onClose={() => setShowAddProduct(false)}
        maxWidth="md"
        fullWidth
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Add />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {selectedProduct ? 'Chỉnh sửa sản phẩm' : 'Thêm sản phẩm mới'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tên sản phẩm"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mô tả"
                  multiline
                  rows={3}
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Giá"
                  type="number"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">VND</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tồn kho"
                  type="number"
                  value={newProduct.stock}
                  onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Danh mục</InputLabel>
                  <Select
                    value={newProduct.category}
                    onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                    label="Danh mục"
                  >
                    <MenuItem value="Áo">Áo</MenuItem>
                    <MenuItem value="Quần">Quần</MenuItem>
                    <MenuItem value="Váy">Váy</MenuItem>
                    <MenuItem value="Giày">Giày</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="URL hình ảnh"
                  value={newProduct.image}
                  onChange={(e) => setNewProduct({ ...newProduct, image: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddProduct(false)}>
              Hủy
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveProduct}
            >
              {selectedProduct ? 'Cập nhật' : 'Thêm'}
            </Button>
          </DialogActions>
        </motion.div>
      </Dialog>
    </Box>
  );
};

export default SellerStore; 
